import { PDFDocument } from 'pdf-lib';
import { isSupportedImageFile } from '../compress/compressImageEngine';
import { needsDecoding, toDisplayableBlob } from './heicDisplay';
import { encodeCanvas } from './imageOutputFormats';

export { isSupportedImageFile };

const PAGE_MARGIN_MM = 10;
const CARD_GAP_MM = 8;
const MM_TO_PT = 72 / 25.4;
const CARD_RENDER_DPI = 300;

const A4_PORTRAIT_PT = { width: 595.28, height: 841.89 };
const A4_LANDSCAPE_PT = { width: 841.89, height: 595.28 };

/** A4 at 300 dpi — the same sheet the PDF prints, as pixels. */
const PAGE_EXPORT_DPI = 300;
const A4_LONG_MM = 297;
const A4_SHORT_MM = 210;

/** Picture formats a finished sheet can be saved as, beside the PDF. */
export const PAGE_IMAGE_FORMATS = ['jpg', 'png', 'webp', 'avif'];

/** White kept around the cards in a picture export — a border, not a page. */
const PAGE_IMAGE_MARGIN_MM = 3;

export function createDocumentPagePdfEngine(cardWidthMm, cardHeightMm) {
    const CARD_W_MM = cardWidthMm;
    const CARD_H_MM = cardHeightMm;

    function resolveOrientation(layout, orientation, copies = 1) {
        const count = Math.min(4, Math.max(1, Number(copies) || 1));
        if (count > 2) return 'portrait';
        if (orientation === 'portrait' || orientation === 'landscape') return orientation;
        if (count >= 3) return 'portrait';
        return layout === 'sideBySide' ? 'landscape' : 'portrait';
    }

    // Rotating a card 90°/270° swaps its footprint (landscape → portrait), so both the
    // layout and the rendered card canvas use these effective dimensions.
    function effectiveCardDims(rotation) {
        const rot = ((Number(rotation) || 0) % 360 + 360) % 360;
        const swap = rot === 90 || rot === 270;
        return { cw: swap ? CARD_H_MM : CARD_W_MM, ch: swap ? CARD_W_MM : CARD_H_MM };
    }

    function computeCopyPairRects(layout, orientation, vPosition, copies, rotation = 0) {
        const count = Math.min(4, Math.max(1, Number(copies) || 1));
        if (count === 1) {
            const single = computeCardLayout(layout, orientation, vPosition, rotation);
            return [{ front: single.front, back: single.back }];
        }

        const { cw: CARD_W, ch: CARD_H } = effectiveCardDims(rotation);
        const isLandscape = orientation === 'landscape';
        const pageW = isLandscape ? 297 : 210;
        const pageH = isLandscape ? 210 : 297;
        const availW = pageW - PAGE_MARGIN_MM * 2;
        const availH = pageH - PAGE_MARGIN_MM * 2;

        const pairW = layout === 'sideBySide' ? CARD_W * 2 + CARD_GAP_MM : CARD_W;
        const pairH = layout === 'sideBySide' ? CARD_H : CARD_H * 2 + CARD_GAP_MM;

        let cols;
        let rows;
        if (isLandscape) {
            if (layout === 'sideBySide') {
                cols = 1;
                rows = count;
            } else {
                cols = 2;
                rows = Math.ceil(count / 2);
            }
        } else if (count > 2 && layout === 'stacked') {
            cols = 2;
            rows = Math.ceil(count / 2);
        } else {
            cols = 1;
            rows = count;
        }

        const cellW = availW / cols;
        const cellH = availH / rows;
        const scale = Math.min(1, cellW / pairW, cellH / pairH);
        const scaledPairW = pairW * scale;
        const scaledPairH = pairH * scale;
        const scaledCardW = CARD_W * scale;
        const scaledCardH = CARD_H * scale;
        const scaledGap = CARD_GAP_MM * scale;

        const pairs = [];
        for (let i = 0; i < count; i += 1) {
            const col = i % cols;
            const row = Math.floor(i / cols);
            const cellX = PAGE_MARGIN_MM + col * cellW + (cellW - scaledPairW) / 2;
            const cellY = PAGE_MARGIN_MM + row * cellH + (cellH - scaledPairH) / 2;

            let front;
            let back;
            if (layout === 'sideBySide') {
                front = { x: cellX, y: cellY, w: scaledCardW, h: scaledCardH };
                back = {
                    x: cellX + scaledCardW + scaledGap,
                    y: cellY,
                    w: scaledCardW,
                    h: scaledCardH,
                };
            } else {
                const offsetX = (scaledPairW - scaledCardW) / 2;
                front = { x: cellX + offsetX, y: cellY, w: scaledCardW, h: scaledCardH };
                back = {
                    x: cellX + offsetX,
                    y: cellY + scaledCardH + scaledGap,
                    w: scaledCardW,
                    h: scaledCardH,
                };
            }
            pairs.push({ front, back });
        }

        return pairs;
    }

    function computeCardLayout(layout, orientation, vPosition, rotation = 0) {
        const { cw: CARD_W, ch: CARD_H } = effectiveCardDims(rotation);
        const isLandscape = orientation === 'landscape';
        const pageW = isLandscape ? 297 : 210;
        const pageH = isLandscape ? 210 : 297;

        const groupW = layout === 'sideBySide' ? CARD_W * 2 + CARD_GAP_MM : CARD_W;
        const groupH = layout === 'sideBySide' ? CARD_H : CARD_H * 2 + CARD_GAP_MM;

        const availW = pageW - PAGE_MARGIN_MM * 2;
        const availH = pageH - PAGE_MARGIN_MM * 2;
        const groupX = PAGE_MARGIN_MM + (availW - groupW) / 2;

        let groupY;
        if (vPosition === 'top') groupY = PAGE_MARGIN_MM;
        else if (vPosition === 'bottom') groupY = pageH - PAGE_MARGIN_MM - groupH;
        else groupY = PAGE_MARGIN_MM + (availH - groupH) / 2;

        let front;
        let back;
        if (layout === 'sideBySide') {
            front = { x: groupX, y: groupY, w: CARD_W, h: CARD_H };
            back = { x: groupX + CARD_W + CARD_GAP_MM, y: groupY, w: CARD_W, h: CARD_H };
        } else {
            front = { x: groupX, y: groupY, w: CARD_W, h: CARD_H };
            back = { x: groupX, y: groupY + CARD_H + CARD_GAP_MM, w: CARD_W, h: CARD_H };
        }

        return { pageW, pageH, isLandscape, front, back };
    }

    // HEIC, TIFF and SVG cannot be drawn as they are — the shared decoder turns
    // each into pixels (and remembers the result, so a preview already decoded
    // is not decoded again here).
    async function prepareFile(file) {
        if (!needsDecoding(file)) return file;
        const blob = await toDisplayableBlob(file);
        if (blob === file) return file;
        const base = file.name.replace(/\.[^.]+$/, '');
        return new File([blob], `${base}.${blob.type === 'image/png' ? 'png' : 'jpg'}`, { type: blob.type });
    }

    async function loadImageFromFile(file) {
        const prepared = await prepareFile(file);
        const url = URL.createObjectURL(prepared);
        try {
            return await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = url;
            });
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    /** Largest w×h with the image's aspect that fits inside frameW×frameH. */
    function containSize(img, frameW, frameH) {
        const ir = img.naturalWidth / img.naturalHeight;
        const fr = frameW / frameH;
        return ir > fr
            ? { w: frameW, h: frameW / ir }
            : { w: frameH * ir, h: frameH };
    }

    function clipRoundRect(ctx, x, y, w, h, r) {
        const radius = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.arcTo(x + w, y, x + w, y + h, radius);
        ctx.arcTo(x + w, y + h, x, y + h, radius);
        ctx.arcTo(x, y + h, x, y, radius);
        ctx.arcTo(x, y, x + w, y, radius);
        ctx.closePath();
        ctx.clip();
    }

    function applySharpen(ctx, canvas, amount) {
        if (amount <= 0) return;
        const w = canvas.width;
        const h = canvas.height;
        const imageData = ctx.getImageData(0, 0, w, h);
        const src = imageData.data;
        // Seed with the original: the kernel skips the outer ring, and an empty buffer
        // would leave those pixels black — a 1px frame around every card.
        const out = new Uint8ClampedArray(src);
        const strength = amount / 40;
        const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];

        for (let y = 1; y < h - 1; y++) {
            for (let x = 1; x < w - 1; x++) {
                for (let c = 0; c < 3; c++) {
                    let sum = 0;
                    let ki = 0;
                    for (let ky = -1; ky <= 1; ky++) {
                        for (let kx = -1; kx <= 1; kx++) {
                            const idx = ((y + ky) * w + (x + kx)) * 4 + c;
                            sum += src[idx] * kernel[ki];
                            ki += 1;
                        }
                    }
                    const idx = (y * w + x) * 4 + c;
                    const base = src[idx];
                    out[idx] = Math.max(0, Math.min(255, base + (sum - base) * strength));
                }
            }
        }

        ctx.putImageData(new ImageData(out, w, h), 0, 0);
    }

    async function renderCardCanvas(img, settings, cornerRadiusPx, slotRotation = 0) {
        const pageRot = ((Number(settings.rotation) || 0) % 360 + 360) % 360;
        // Per-face turn rides on top of the page rotation: it spins the photo inside the
        // card, so the card footprint (and therefore the page layout) never changes.
        const rot = ((pageRot + (Number(slotRotation) || 0)) % 360 + 360) % 360;
        const pageSwap = pageRot === 90 || pageRot === 270;
        const swap = rot === 90 || rot === 270;
        const scale = CARD_RENDER_DPI / 25.4;
        // The card slot on the page, in mm — the page rotation decides its footprint.
        const slotWmm = pageSwap ? CARD_H_MM : CARD_W_MM;
        const slotHmm = pageSwap ? CARD_W_MM : CARD_H_MM;
        // The frame the photo is laid into before its own turn.
        const frameWmm = swap ? slotHmm : slotWmm;
        const frameHmm = swap ? slotWmm : slotHmm;
        const fit = containSize(img, frameWmm, frameHmm);
        // The canvas IS the photo, not the card slot: a photo that does not fill the slot
        // would otherwise embed white margins, and that white block's edge prints as a
        // visible border around the card.
        const outWmm = swap ? fit.h : fit.w;
        const outHmm = swap ? fit.w : fit.h;
        const width = Math.round(outWmm * scale);
        const height = Math.round(outHmm * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        ctx.save();
        if (cornerRadiusPx > 0) clipRoundRect(ctx, 0, 0, width, height, cornerRadiusPx);
        ctx.filter = `brightness(${settings.brightness}%) contrast(${settings.contrast}%)`;
        ctx.translate(width / 2, height / 2);
        if (rot) ctx.rotate((rot * Math.PI) / 180);
        ctx.drawImage(img, (-fit.w * scale) / 2, (-fit.h * scale) / 2, fit.w * scale, fit.h * scale);
        ctx.restore();

        if (settings.sharpen > 0) applySharpen(ctx, canvas, settings.sharpen);
        return { canvas, wMm: outWmm, hMm: outHmm, slotWmm };
    }

    /** Place the photo centred inside its card rect, shrunk by whatever the layout shrank. */
    function cardRectOnPage(card, rect) {
        const s = rect.w / card.slotWmm;
        const w = card.wMm * s;
        const h = card.hMm * s;
        return { x: rect.x + (rect.w - w) / 2, y: rect.y + (rect.h - h) / 2, w, h };
    }

    function drawCardOnPage(ctx, card, rect, mmScale) {
        const r = cardRectOnPage(card, rect);
        ctx.drawImage(card.canvas, r.x * mmScale, r.y * mmScale, r.w * mmScale, r.h * mmScale);
    }

    /**
     * The A4 sheet as a canvas, plus WHERE on it the cards actually landed (in
     * mm). The bounds are what lets a picture export trim the empty paper the PDF
     * needs and an image does not.
     */
    async function renderPage(frontFile, backFile, settings, targetWidth = 794) {
        const copies = Math.min(4, Math.max(1, settings.copies || 1));
        const orientation = resolveOrientation(settings.layout, settings.orientation, copies);
        const pairRects = computeCopyPairRects(settings.layout, orientation, settings.vPosition, copies, settings.rotation);
        const isLandscape = orientation === 'landscape';
        const pageWmm = isLandscape ? 297 : 210;
        const pageHmm = isLandscape ? 210 : 297;
        const targetHeight = Math.round(targetWidth * (pageHmm / pageWmm));
        const mmScale = targetWidth / pageWmm;
        const cornerRadiusPx = settings.roundedCorners ? 14 : 0;

        const frontImg = frontFile ? await loadImageFromFile(frontFile) : null;
        const backImg = backFile ? await loadImageFromFile(backFile) : null;
        const frontCard = frontImg ? await renderCardCanvas(frontImg, settings, cornerRadiusPx, settings.rotationFront) : null;
        const backCard = backImg ? await renderCardCanvas(backImg, settings, cornerRadiusPx, settings.rotationBack) : null;

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        // Where each COPY landed, in page mm — the box around its front and back
        // together. A picture export repacks the sheet from these, so it never has
        // to re-derive the layout maths.
        const pairBoxesMm = [];
        for (const pair of pairRects) {
            let box = null;
            for (const [card, rect] of [[frontCard, pair.front], [backCard, pair.back]]) {
                if (!card) continue;
                drawCardOnPage(ctx, card, rect, mmScale);
                const r = cardRectOnPage(card, rect);
                box = box
                    ? {
                        x: Math.min(box.x, r.x),
                        y: Math.min(box.y, r.y),
                        right: Math.max(box.right, r.x + r.w),
                        bottom: Math.max(box.bottom, r.y + r.h),
                    }
                    : { x: r.x, y: r.y, right: r.x + r.w, bottom: r.y + r.h };
            }
            if (box) pairBoxesMm.push({ x: box.x, y: box.y, w: box.right - box.x, h: box.bottom - box.y });
        }

        return { canvas, pairBoxesMm, mmScale, pageWmm, pageHmm };
    }

    async function renderPreviewCanvas(frontFile, backFile, settings, targetWidth = 794) {
        const { canvas } = await renderPage(frontFile, backFile, settings, targetWidth);
        return canvas;
    }

    async function canvasToJpegBlob(canvas, quality = 0.92) {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/jpeg', quality);
        });
    }

    /**
     * The finished sheet as a picture — the same A4 layout the PDF draws, at
     * print resolution, in whichever format was asked for.
     *
     * Encoding goes through the shared `encodeCanvas`, so these two tools inherit
     * what every other image tool already knows: AVIF through WebAssembly where
     * the canvas cannot write it, and an honest extension when a browser hands
     * back something other than what was asked for.
     *
     * @returns {Promise<{ blob: Blob, format: string, ext: string }>}
     */
    async function createPageImage(frontFile, backFile, settings, format = 'png') {
        const copies = Math.min(4, Math.max(1, settings.copies || 1));
        const orientation = resolveOrientation(settings.layout, settings.orientation, copies);
        const widthMm = orientation === 'landscape' ? A4_LONG_MM : A4_SHORT_MM;
        const targetWidth = Math.round((widthMm / 25.4) * PAGE_EXPORT_DPI);

        const { canvas, pairBoxesMm, mmScale } = await renderPage(frontFile, backFile, settings, targetWidth);
        if (!pairBoxesMm.length) return encodeCanvas(canvas, format, { quality: 0.94 });

        /**
         * A picture is not a sheet of paper.
         *
         * The A4 layout spreads the copies down the page — that is right for
         * printing and wrong for a JPG, where the gaps open up as bands of white
         * and the file reads as mostly nothing. So each copy is lifted off the
         * rendered sheet and repacked on a canvas sized to the copies themselves:
         * the same grid, a single card gap between them, and a thin white edge
         * all round. Two copies come out as two cards, not as an A4 with two
         * cards on it.
         */
        const cols = new Map();
        const rows = new Map();
        const bucket = (map, mm) => {
            // Boxes in the same column share an x to within rounding; 1mm apart is
            // the same column, and no layout puts two columns that close.
            const hit = [...map.keys()].find((k) => Math.abs(k - mm) < 1);
            if (hit === undefined) map.set(mm, 0);
            return hit === undefined ? mm : hit;
        };
        const placed = pairBoxesMm.map((box) => ({
            box,
            col: bucket(cols, box.x),
            row: bucket(rows, box.y),
        }));
        const colKeys = [...cols.keys()].sort((a, b) => a - b);
        const rowKeys = [...rows.keys()].sort((a, b) => a - b);

        const cellW = Math.max(...pairBoxesMm.map((b) => b.w));
        const cellH = Math.max(...pairBoxesMm.map((b) => b.h));
        const gap = CARD_GAP_MM;
        const margin = PAGE_IMAGE_MARGIN_MM;
        const outWmm = colKeys.length * cellW + (colKeys.length - 1) * gap + margin * 2;
        const outHmm = rowKeys.length * cellH + (rowKeys.length - 1) * gap + margin * 2;

        const out = document.createElement('canvas');
        out.width = Math.max(1, Math.round(outWmm * mmScale));
        out.height = Math.max(1, Math.round(outHmm * mmScale));
        const ctx = out.getContext('2d');
        // White behind it: the padding then matches the card's own paper, and a
        // format without alpha has nothing to guess at.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);

        for (const { box, col, row } of placed) {
            const ci = colKeys.indexOf(col);
            const ri = rowKeys.indexOf(row);
            // Centred in its cell, so a copy narrower than the widest still lines
            // up with the rest instead of hugging the left edge.
            const destXmm = margin + ci * (cellW + gap) + (cellW - box.w) / 2;
            const destYmm = margin + ri * (cellH + gap) + (cellH - box.h) / 2;
            ctx.drawImage(
                canvas,
                Math.round(box.x * mmScale), Math.round(box.y * mmScale),
                Math.round(box.w * mmScale), Math.round(box.h * mmScale),
                Math.round(destXmm * mmScale), Math.round(destYmm * mmScale),
                Math.round(box.w * mmScale), Math.round(box.h * mmScale),
            );
        }

        return encodeCanvas(out, format, { quality: 0.94 });
    }

    async function createPdf(frontFile, backFile, settings) {
        const copies = Math.min(4, Math.max(1, settings.copies || 1));
        const orientation = resolveOrientation(settings.layout, settings.orientation, copies);
        const pairRects = computeCopyPairRects(settings.layout, orientation, settings.vPosition, copies, settings.rotation);
        const pageSize = orientation === 'landscape' ? A4_LANDSCAPE_PT : A4_PORTRAIT_PT;
        const cornerRadiusPx = settings.roundedCorners ? Math.round(14 * (CARD_RENDER_DPI / 96)) : 0;

        const frontImg = await loadImageFromFile(frontFile);
        const backImg = await loadImageFromFile(backFile);
        const frontCard = await renderCardCanvas(frontImg, settings, cornerRadiusPx, settings.rotationFront);
        const backCard = await renderCardCanvas(backImg, settings, cornerRadiusPx, settings.rotationBack);

        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([pageSize.width, pageSize.height]);
        const frontJpeg = new Uint8Array(await (await canvasToJpegBlob(frontCard.canvas, 0.92)).arrayBuffer());
        const backJpeg = new Uint8Array(await (await canvasToJpegBlob(backCard.canvas, 0.92)).arrayBuffer());
        const frontEmbedded = await pdfDoc.embedJpg(frontJpeg);
        const backEmbedded = await pdfDoc.embedJpg(backJpeg);

        for (const pair of pairRects) {
            for (const [embedded, card, rect] of [
                [frontEmbedded, frontCard, pair.front],
                [backEmbedded, backCard, pair.back],
            ]) {
                const r = cardRectOnPage(card, rect);
                page.drawImage(embedded, {
                    x: r.x * MM_TO_PT,
                    y: pageSize.height - (r.y + r.h) * MM_TO_PT,
                    width: r.w * MM_TO_PT,
                    height: r.h * MM_TO_PT,
                });
            }
        }

        const bytes = await pdfDoc.save();
        return new Blob([bytes], { type: 'application/pdf' });
    }

    return {
        resolveOrientation,
        computeCardLayout,
        computeCopyPairRects,
        loadImageFromFile,
        renderPreviewCanvas,
        createPdf,
        createPageImage,
    };
}
